import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PackageOpen, ArrowLeft, Eye, ShoppingBag, Package } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const zl = (g) => `${(((g || 0) / 100)).toFixed(2).replace(".", ",")} zł`;

export default function MyStorePreview() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    api.get(`/b2b/admin/stores/${storeId}/preview`)
      .then((r) => { setStore(r.data.store); setProducts(r.data.products || []); })
      .catch((e) => setErr(errMsg(e)))
      .finally(() => setLoading(false));
  }, [storeId]);

  return (
    <div
      data-testid="redfox-preview"
      className="min-h-screen flex flex-col text-neutral-100 overflow-x-hidden"
      style={{ fontFamily: "'Inter','Helvetica Neue',sans-serif", backgroundColor: "#0a0a0b", backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,0.02) 0 2px, transparent 2px 9px)" }}
    >
      {/* Pasek podglądu administratora */}
      <div data-testid="redfox-preview-banner" className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-amber-400 px-4 py-2 text-[#0D1117]">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="h-4 w-4 shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wider truncate">
            Podgląd administratora — ten sklep nie jest widoczny dla klientów{store && !store.active ? " (roboczy / ukryty)" : ""}
          </span>
        </div>
        <button data-testid="redfox-preview-back" onClick={() => navigate("/?tab=b2b")}
          className="flex items-center gap-1.5 rounded-md bg-[#0D1117] px-3 py-1 text-xs font-bold text-amber-400 hover:bg-[#0D1117]/80 transition-colors shrink-0">
          <ArrowLeft className="h-3.5 w-3.5" />Wróć do panelu
        </button>
      </div>

      <div className="h-1 w-full" style={{ background: "#E4122A" }} />

      {/* Nagłówek storefrontu (jak widzi klient) */}
      <header className="sticky top-[38px] z-40 border-b border-red-600/30 bg-[#0a0a0b]/90 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {store?.logo_file_id
              ? <img src={`${API}/files/${store.logo_file_id}`} alt={store?.name || "Sklep"} className="h-9 w-auto max-w-[160px] object-contain" />
              : <span className="uppercase tracking-[0.18em] text-xl truncate italic" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{store?.name || "Mój sklep"}</span>}
          </div>
          <nav className="flex items-center gap-2 sm:gap-4 opacity-60 pointer-events-none select-none">
            <span className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-neutral-300"><Package className="h-4 w-4" />Zamówienia</span>
            <span className="relative flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-white font-semibold text-sm"><ShoppingBag className="h-4 w-4" />Koszyk</span>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        {/* Baner kolekcji */}
        {store?.banner_file_id ? (
          <div className="relative w-full overflow-hidden bg-neutral-900">
            <img src={`${API}/files/${store.banner_file_id}`} alt="" className="block w-full h-auto" />
          </div>
        ) : (
          <div className="relative w-full overflow-hidden py-16 sm:py-24" style={{ background: "linear-gradient(120deg,#0a0a0b 0%,#1a0608 60%,#0a0a0b 100%)" }}>
            <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: "repeating-linear-gradient(115deg, rgba(228,18,42,0.45) 0 3px, transparent 3px 30px)" }} />
            <div className="pointer-events-none absolute -left-12 top-0 h-full w-44 -skew-x-12" style={{ background: "#E4122A", opacity: 0.9 }} />
            <div className="pointer-events-none absolute -right-20 bottom-0 h-24 w-96 skew-x-12" style={{ background: "linear-gradient(90deg,transparent,#E4122A)", opacity: 0.55 }} />
            <div className="relative mx-auto max-w-6xl px-6">
              <span className="inline-block bg-[#E4122A] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-white" style={{ clipPath: "polygon(0 0,100% 0,92% 100%,0% 100%)" }}>Oficjalny sklep</span>
              <h1 className="mt-4 uppercase italic leading-[0.92] text-5xl sm:text-7xl" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{store?.name || "Kolekcja"}</h1>
              <p className="mt-4 text-neutral-400 text-sm uppercase tracking-[0.2em]">Kolekcja przygotowana dla zespołu</p>
            </div>
          </div>
        )}

        <div className="mx-auto max-w-6xl px-4 py-10">
          {loading ? (
            <p className="text-neutral-500 py-20 text-center">Ładowanie…</p>
          ) : err ? (
            <div className="grid place-items-center py-24 text-neutral-400" data-testid="redfox-preview-error">
              <p className="font-semibold text-red-400">{err}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="grid place-items-center py-24 text-neutral-500" data-testid="redfox-preview-empty">
              <PackageOpen className="h-12 w-12 mb-4" />
              <p className="font-semibold">Brak opublikowanych produktów</p>
              <p className="text-sm">Dodaj produkty w panelu i opublikuj je, aby pojawiły się w sklepie.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="redfox-preview-grid">
              {products.map((p) => (
                <button key={p.id} type="button" onClick={() => setSel(p)} data-testid={`redfox-preview-product-${p.id}`}
                  className="group relative block w-full text-left overflow-hidden rounded-xl bg-neutral-900 ring-1 ring-white/5 transition hover:ring-red-600/50">
                  <div className="relative aspect-[4/5] overflow-hidden bg-neutral-800">
                    {p.photos?.[0]
                      ? <img src={`${API}/files/${p.photos[0]}`} alt={p.name} className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" />
                      : <div className="grid h-full place-items-center text-neutral-600"><PackageOpen className="h-10 w-10" /></div>}
                    <span className="absolute bottom-2 right-2 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white opacity-0 transition group-hover:opacity-100">Zobacz produkt</span>
                  </div>
                  <div className="p-3 sm:p-4">
                    <p className="font-semibold leading-tight line-clamp-2">{p.name}</p>
                    {p.subtitle && <p className="mt-0.5 text-xs text-neutral-400 line-clamp-1">{p.subtitle}</p>}
                    <p className="mt-2 text-xl tracking-wide" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{zl(p.price_grosz)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-red-600/20 py-8 text-center text-xs text-neutral-500">
        <p>{store?.name || "Sklep"} — podgląd administratora (EMPRO).</p>
      </footer>
      {sel && <PreviewProductModal product={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

function PreviewProductModal({ product: p, onClose }) {
  const [main, setMain] = useState((p.photos || [])[0] || "");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="redfox-preview-product-modal" className="max-w-3xl max-h-[92vh] overflow-y-auto border-white/10 bg-neutral-950 text-neutral-100">
        <DialogHeader>
          <DialogTitle className="uppercase italic" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{p.name}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <div className="aspect-[4/5] overflow-hidden rounded-xl bg-neutral-900 p-2" data-testid="redfox-preview-modal-main">
              {main ? <img src={`${API}/files/${main}`} alt={p.name} className="h-full w-full object-contain" /> : <div className="grid h-full place-items-center text-neutral-600"><PackageOpen className="h-10 w-10" /></div>}
            </div>
            {(p.photos || []).length > 1 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {p.photos.map((ph) => (
                  <button key={ph} type="button" onClick={() => setMain(ph)} className={`h-14 w-14 overflow-hidden rounded-lg ring-2 transition ${main === ph ? "ring-red-600" : "ring-transparent"}`}>
                    <img src={`${API}/files/${ph}`} alt="" className="h-full w-full object-contain bg-neutral-900" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            {p.subtitle && <p className="text-neutral-400">{p.subtitle}</p>}
            <p className="mt-2 text-2xl" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{zl(p.price_grosz)}</p>
            {p.description && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-neutral-300">{p.description}</p>}
            {(p.colors || []).length > 0 && (
              <div className="mt-4">
                <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-neutral-400">Kolory</p>
                <div className="flex flex-wrap gap-1.5">{p.colors.map((c) => <span key={c} className="rounded-full bg-white/5 px-3 py-1 text-xs">{c}</span>)}</div>
              </div>
            )}
            {(p.sizes || []).length > 0 && (
              <div className="mt-4">
                <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-neutral-400">Rozmiary</p>
                <div className="flex flex-wrap gap-1.5">{p.sizes.map((s) => <span key={s} className="min-w-9 rounded-md border border-white/15 px-2.5 py-1 text-center text-xs font-bold">{s}</span>)}</div>
              </div>
            )}
            {p.size_table && (
              <div className="mt-4">
                <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-neutral-400">Tabela rozmiarów</p>
                <pre className="whitespace-pre-wrap rounded-xl bg-neutral-900 p-3 font-mono text-xs text-neutral-300">{p.size_table}</pre>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
